import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"

import { createPayloadReducer } from "@/helpers/redux"
import Api from "@/api"
import OnScreenNotifications, { newAxiosErrorHandler } from "@/osn"
import routes from "@/routes"

import { formatDate, FORMATS as DATE_FORMATS } from "@/components/DatePickers"

const createOperatorApplicationSlice = ({ name }) => {
  const updateOperatorDetails = createAsyncThunk(
    `${name}/updateOperatorDetails`,
    async ({ values }, thunkAPI) => {
      const { operatorApplicationId } = thunkAPI.getState()[name]

      return Api.operatorApplication
        .updateOperatorDetails(operatorApplicationId, values)
        .catch(newAxiosErrorHandler(thunkAPI.dispatch))
        .then((response) => {
          thunkAPI.dispatch(
            OnScreenNotifications.success(response.data.message, { duration: 1500 })
          )
          return response
        })
        .then((response) => ({ operator: response.data.data.operator }))
    }
  )

  const updateOperatorQuestionnaire = createAsyncThunk(
    `${name}/updateOperatorQuestionnaire`,
    async ({ values }, thunkAPI) => {
      const { operatorApplicationId } = thunkAPI.getState()[name]

      return Api.operatorApplication
        .updateOperatorQuestionnaire(operatorApplicationId, values)
        .catch(newAxiosErrorHandler(thunkAPI.dispatch))
        .then((response) => {
          thunkAPI.dispatch(
            OnScreenNotifications.success(response.data.message, { duration: 1500 })
          )
          return response
        })
        .then((response) => ({ operatorQuestionnaire: response.data.data.operatorQuestionnaire }))
    }
  )

  const submitAgreement =
    ({ signature }) =>
    async (dispatch, getState) => {
      const { operatorApplicationId, agreement } = getState()[name]

      return Api.operatorApplication
        .submitAgreement(operatorApplicationId, {
          signature,
          document_agreements: agreement.documents,
          agreement_text: agreement.agreementTextUnformatted,
        })
        .then((_response) => {
          window.location = routes.onboarding_operator_profiles_path()
        }, newAxiosErrorHandler(dispatch))
    }

  const cancelApplication = () => async (dispatch, getState) => {
    const { operatorApplicationId } = getState()[name]

    return Api.operatorApplication.cancel(operatorApplicationId).then((response) => {
      dispatch(OnScreenNotifications.success(response.data.message, { duration: 1500 }))
      window.location = routes.onboarding_operator_profiles_path()
    }, newAxiosErrorHandler(dispatch))
  }

  const retractApplication =
    ({ reason }) =>
    async (dispatch, getState) => {
      const { operatorApplicationId } = getState()[name]

      return Api.operatorApplication.retract(operatorApplicationId, reason).then((response) => {
        dispatch(OnScreenNotifications.success(response.data.message, { duration: 1500 }))
        // Retract can be triggered from either the standalone operator_application
        // show page or the embedded view on the operator_profile detail page. The
        // profile page's adapter hardcodes mode: :review for the embedded application
        // (app/read_models/onboarding_operator_profile_view/adapter.rb), which sets
        // context.readOnly = true in the wizard and hides the Sign button — so a
        // simple window.location.reload() would land the operator on a dead-end
        // page they can't proceed from. Redirect to the standalone application show
        // page instead: that page's controller (Onboarding::OperatorApplicationsController#show)
        // picks mode based on the read model's editable? predicate, so a just-retracted
        // (now :pending) application correctly renders in :edit mode with the Sign
        // affordance available.
        window.location = routes.onboarding_operator_application_path({
          id: operatorApplicationId,
          _options: true,
        })
      }, newAxiosErrorHandler(dispatch))
    }

  const slice = createSlice({
    name,
    initialState: {
      initialized: false,
      submitting: false,
      operatorApplicationId: null,
      operator: {},
      agreement: {},
      portDetailsForm: {
        selectedPorts: [],
      },
    },
    reducers: {
      initialize: createPayloadReducer({ persistentState: { initialized: true } }),
      updatePortDetailsForm: createPayloadReducer({ key: "portDetailsForm" }),
      agreeToAllDocuments: (state) => {
        const docs = _.cloneDeep(state.agreement.documents)

        _.each(docs, (d) => {
          d.agreed = true
          d.agreedOnDate = formatDate(new Date(), DATE_FORMATS.humaneShort)
          d.agreedByUser = state.currentUserName
        })

        return {
          ...state,
          agreement: {
            ...state.agreement,
            documents: docs,
          },
        }
      },
      agreeToDocument: (state, { payload }) => {
        const docs = _.cloneDeep(state.agreement.documents)

        _.each(docs, (d) => {
          if (d.id == payload.documentId) {
            if (d.agreed) {
              d.agreed = false
              d.agreedOnDate = ""
              d.agreedByUser = ""
            } else {
              d.agreed = true
              d.agreedOnDate = formatDate(new Date(), DATE_FORMATS.humaneShort)
              d.agreedByUser = state.currentUserName
            }
          }
          d
        })

        return {
          ...state,
          agreement: {
            ...state.agreement,
            documents: docs,
          },
        }
      },
    },
    extraReducers: {
      [updateOperatorDetails.pending]: (state) => ({
        ...state,
        submitting: true,
      }),
      [updateOperatorDetails.rejected]: (state) => ({
        ...state,
        submitting: false,
      }),
      [updateOperatorDetails.fulfilled]: (state, { payload }) => ({
        ...state,
        submitting: false,
        operator: payload.operator,
      }),
      [updateOperatorQuestionnaire.pending]: (state) => ({
        ...state,
        submitting: false,
      }),
      [updateOperatorQuestionnaire.rejected]: (state) => ({
        ...state,
        submitting: false,
      }),
      [updateOperatorQuestionnaire.fulfilled]: (state, { payload }) => ({
        ...state,
        submitting: false,
        operatorQuestionnaire: payload.operatorQuestionnaire,
      }),
    },
  })

  // I'm lazy and in our case I don't see any need to have a special import for thunks...
  slice.actions = _.merge({}, slice.actions, {
    updateOperatorDetails,
    updateOperatorQuestionnaire,
    submitAgreement,
    cancelApplication,
    retractApplication,
  })

  return slice
}

export default createOperatorApplicationSlice
